"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import type { MarketsCategory, MarketsPanelColumnItem } from "@/config/nav-links";
import { navIcons } from "./nav-icons";
import { cn } from "@/lib/utils";
import { useNavMobileThemeStyles, useNavThemeStyles, type NavThemeStyles } from "./nav-theme-context";

type MarketsMegaMenuProps = {
    sidebarTitle?: string;
    categories: MarketsCategory[];
    onNavigate?: () => void;
    variant?: "desktop" | "mobile";
    /** Desktop: reset sidebar panel when the parent nav dropdown closes */
    isOpen?: boolean;
};

const flattenColumns = (columns: MarketsPanelColumnItem[][]) => columns.flat();

const panelItemClassName =
    "group flex w-full items-center gap-2 rounded-[var(--radius-m)] px-2 py-3 text-ink-header no-underline outline-none transition-colors hover:bg-neutral-50 hover:font-bold";

const PanelItemChevron = ({ alwaysVisible }: { alwaysVisible?: boolean }) => (
    <ChevronRight
        className={cn(
            "size-6 shrink-0 text-cta-primary transition-opacity",
            alwaysVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
        )}
        strokeWidth={1.5}
        aria-hidden
    />
);

const hasPanelItems = (category: MarketsCategory) =>
    flattenColumns(category.panel?.columns ?? []).length > 0;

type CategoryRowProps = {
    category: MarketsCategory;
    isActive: boolean;
    variant: "desktop" | "mobile";
    theme: NavThemeStyles;
    onHover: () => void;
    onSelect: () => void;
    onNavigate?: () => void;
};

const CategoryRow = ({
    category,
    isActive,
    variant,
    theme,
    onHover,
    onSelect,
    onNavigate,
}: CategoryRowProps) => {
    const Icon = category.icon ? navIcons[category.icon] : null;
    const showPanel = hasPanelItems(category);
    const className =
        variant === "mobile"
            ? cn(
                  "flex w-full items-center gap-3 text-left text-xl leading-[26px] transition-colors",
                  theme.marketsCategory,
                  isActive
                      ? cn("rounded-[var(--radius-m)] p-3 font-semibold", theme.marketsCategoryActive)
                      : "rounded-[44px] px-4 py-3 font-regular",
              )
            : cn(
                  "flex w-full items-center gap-3 rounded-[var(--radius-m)] px-4 py-3 text-left text-base leading-[22px] no-underline transition-colors",
                  theme.marketsCategory,
                  isActive ? theme.marketsCategoryActive : "font-regular",
              );

    const label = (
        <>
            {Icon && (
                <Icon className="size-6 shrink-0" strokeWidth={1.5} aria-hidden />
            )}
            <span>{category.label}</span>
        </>
    );

    const newTabProps = category.openInNewTab
        ? { target: "_blank" as const, rel: "noopener noreferrer" }
        : {};

    // Desktop: every category row drives which panel is shown on hover.
    const hoverProps =
        variant === "desktop" || showPanel ? { onMouseEnter: onHover } : {};

    // Follow `href` on both desktop and mobile when set (Indices → /indices, Share Market Today).
    // Desktop hover still reveals panels; mobile keeps the active panel open by default.
    // Categories without href (e.g. Popular Stocks) remain expand-only buttons.
    const followHrefOnClick = Boolean(category.href);

    if (followHrefOnClick && category.href) {
        return (
            <Link
                href={category.href}
                className={className}
                onClick={onNavigate}
                {...hoverProps}
                {...newTabProps}
            >
                {label}
            </Link>
        );
    }

    return (
        <button
            type="button"
            className={className}
            onClick={onSelect}
            {...hoverProps}
        >
            {label}
        </button>
    );
};

const getDefaultDesktopLabel = (items: MarketsCategory[]) =>
    items.find((category) => category.label === "Indices")?.label ??
    items[0]?.label ??
    null;

const MarketsMegaMenu = ({
    sidebarTitle = "MARKETS",
    categories,
    onNavigate,
    variant = "desktop",
    isOpen = true,
}: MarketsMegaMenuProps) => {
    const desktopTheme = useNavThemeStyles();
    const mobileTheme = useNavMobileThemeStyles();
    const theme = variant === "mobile" ? mobileTheme : desktopTheme;
    const defaultDesktopLabel = getDefaultDesktopLabel(categories);
    const [desktopActiveLabel, setDesktopActiveLabel] = useState<string | null>(
        defaultDesktopLabel,
    );
    const [mobileActiveLabel, setMobileActiveLabel] = useState(
        () => categories[0]?.label ?? "",
    );

    useEffect(() => {
        if (variant !== "desktop") return;
        // Web: each time Markets opens, select Indices (and its panel) by default.
        if (isOpen) {
            setDesktopActiveLabel(defaultDesktopLabel);
        }
    }, [isOpen, variant, defaultDesktopLabel]);

    if (variant === "mobile") {
        const mobileActiveCategory =
            categories.find((category) => category.label === mobileActiveLabel) ??
            categories[0];

        if (!mobileActiveCategory) return null;

        return (
            <div className={cn("flex w-full flex-col gap-6 p-4", theme.marketsShell)}>
                <p
                    className={cn(
                        "text-base font-regular uppercase leading-[22px] tracking-[var(--typography-font-letter-spacing-3xl)]",
                        theme.marketsSidebarTitle,
                    )}
                >
                    {sidebarTitle}
                </p>

                <ul className="flex w-full flex-col gap-6">
                    {categories.map((category) => {
                        const isActive = category.label === mobileActiveCategory.label;
                        const panelItems = flattenColumns(category.panel?.columns ?? []);
                        const showPanel = isActive && panelItems.length > 0;

                        return (
                            <li key={category.label} className="flex w-full flex-col gap-4">
                                <CategoryRow
                                    category={category}
                                    isActive={isActive}
                                    variant="mobile"
                                    theme={theme}
                                    onHover={() => setMobileActiveLabel(category.label)}
                                    onSelect={() => setMobileActiveLabel(category.label)}
                                    onNavigate={onNavigate}
                                />

                                {showPanel && (
                                    <div
                                        className={cn(
                                            "flex w-full flex-col gap-5 overflow-hidden rounded-2xl p-5",
                                            theme.marketsPanel,
                                        )}
                                    >
                                        <p className="text-base font-semibold uppercase leading-[22px] tracking-[2px] text-ink-header">
                                            {category.panel?.title}
                                        </p>
                                        <ul className="flex w-full flex-col gap-4">
                                            {panelItems.map((item) => (
                                                <li key={item.label}>
                                                    <Link
                                                        href={item.href}
                                                        onClick={onNavigate}
                                                        {...(item.openInNewTab
                                                            ? {
                                                                  target: "_blank" as const,
                                                                  rel: "noopener noreferrer",
                                                              }
                                                            : {})}
                                                        className="block w-full text-xl font-regular leading-[26px] text-ink-header no-underline"
                                                    >
                                                        {item.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }

    const activeCategory =
        desktopActiveLabel != null
            ? categories.find((category) => category.label === desktopActiveLabel)
            : undefined;
    const columns = activeCategory?.panel?.columns ?? [];
    const activePanelItems = flattenColumns(columns);
    const showDesktopPanel = activePanelItems.length > 0;

    return (
        <div
            className={cn(
                "flex w-full min-h-[466px] items-stretch gap-4 p-4",
                theme.marketsShell,
            )}
            onMouseLeave={() => setDesktopActiveLabel(getDefaultDesktopLabel(categories))}
        >
            <div className="flex w-full max-w-[290px] shrink-0 flex-col gap-6">
                <p
                    className={cn(
                        "text-base font-semibold uppercase leading-[22px] tracking-[var(--typography-font-letter-spacing-3xl)]",
                        theme.marketsSidebarTitle,
                    )}
                >
                    {sidebarTitle}
                </p>
                <ul className="flex w-full flex-col gap-1">
                    {categories.map((category) => {
                        const isActive = category.label === desktopActiveLabel;

                        return (
                            <li key={category.label}>
                                <CategoryRow
                                    category={category}
                                    isActive={isActive}
                                    variant="desktop"
                                    theme={theme}
                                    onHover={() => setDesktopActiveLabel(category.label)}
                                    onSelect={() => setDesktopActiveLabel(category.label)}
                                    onNavigate={onNavigate}
                                />
                            </li>
                        );
                    })}
                </ul>
            </div>

            {showDesktopPanel && activeCategory ? (
                <div
                    className={cn(
                        "relative flex min-h-[290px] min-w-0 flex-1 flex-col gap-8 overflow-hidden rounded-2xl p-10",
                        theme.marketsPanel,
                    )}
                >
                    <div
                        aria-hidden
                        className={cn(
                            "pointer-events-none absolute -right-6 -top-10 size-[140px] rounded-full blur-2xl",
                            theme.marketsPanelGlow,
                        )}
                    />

                    <p className="relative text-2xl font-semibold uppercase leading-8 tracking-[var(--typography-font-letter-spacing-3xl)] text-ink-header">
                        {activeCategory.panel?.title}
                    </p>

                    <div className="relative flex w-full items-start">
                        {columns.map((column, columnIndex) => (
                            <div key={columnIndex} className="flex min-w-0 flex-1 items-start">
                                {columnIndex > 0 && (
                                    <div
                                        aria-hidden
                                        className="mx-[37.5px] hidden h-[290px] w-px shrink-0 bg-surface-stroke sm:block"
                                    />
                                )}
                                <ul className="flex w-full flex-col">
                                    {column.map((item) => (
                                        <li key={item.label}>
                                            <Link
                                                href={item.href}
                                                onClick={onNavigate}
                                                {...(item.openInNewTab
                                                    ? {
                                                          target: "_blank" as const,
                                                          rel: "noopener noreferrer",
                                                      }
                                                    : {})}
                                                className={cn(
                                                    panelItemClassName,
                                                    "text-base leading-[22px]",
                                                    item.featured && "font-bold",
                                                )}
                                            >
                                                {item.label}
                                                <PanelItemChevron alwaysVisible={item.featured} />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default MarketsMegaMenu;
